import { AlertTriangle, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { Doctor } from "@/types/doctor";

const MAX_REASON = 500;

export function RejectDoctorDialog({
  doctor,
  open,
  onOpenChange,
  onConfirm,
  pending,
}: {
  doctor: Doctor | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void;
  pending: boolean;
}) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  if (!doctor) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <AlertTriangle className="size-5 text-warning" aria-hidden="true" />
            Reject {doctor.name}
          </DialogTitle>
          <DialogDescription>
            The doctor stays hidden from the HealNova patient application and can be reviewed again
            later.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-warning/25 bg-warning/8 p-3 text-xs leading-relaxed text-warning">
          This action changes the doctor's status immediately. Double-check you have the right
          registration before continuing.
        </div>

        <div className="space-y-2">
          <Label htmlFor="reject-reason">Reason (optional)</Label>
          <Textarea
            id="reject-reason"
            value={reason}
            maxLength={MAX_REASON}
            onChange={(event) => setReason(event.target.value)}
            placeholder="e.g. Could not verify credentials"
            className="min-h-28 rounded-xl"
          />
          <div className="flex items-start justify-between gap-4">
            <p className="text-xs text-muted-foreground">
              Reason is optional. The current backend accepts this field but does not permanently
              store it.
            </p>
            <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
              {reason.length}/{MAX_REASON}
            </span>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => onConfirm(reason)} disabled={pending}>
            {pending && <Loader2 className="size-4 animate-spin" />}
            Reject Doctor
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
